/* eslint-disable no-console */
const __DEBUG__ = import.meta.dev || false;

function debugLog(...args: unknown[]) {
  if (__DEBUG__) {
    console.info(...args);
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const refreshHandler = new DefaultRefreshHandler();

  nuxtApp.hook("app:mounted", () => {
    debugLog("🚤 Auth Plugin - initializing on mount");
    // Run init without awaiting to avoid blocking UI
    refreshHandler.init();
  });

  const originalUnmount = nuxtApp.vueApp.unmount;
  nuxtApp.vueApp.unmount = function () {
    debugLog("🚤 Auth Plugin - destroying in unmount");
    refreshHandler.destroy();
    originalUnmount();
  };
});

export class DefaultRefreshHandler {
  /** Result of `useUserSession` composable, is reactive */
  private userSession?: ReturnType<typeof useUserSession>;

  /** Refetch timer */
  private refetchIntervalTimer?: ReturnType<typeof setInterval>;

  /** Stop handle for the watcher */
  private stopVisibilityWatcher?: () => void;

  /** Because passing `this.visibilityHandler` to `watch` loses `this` context */
  private boundVisibilityHandler: () => Promise<void>;

  private documentVisibility: ReturnType<typeof useDocumentVisibility>;

  /** Timestamp of the last session refresh */
  private lastRefreshTimestamp = 0;

  /** Minimum time (in milliseconds) between visibility-triggered refreshes */
  private readonly visibilityRefreshThrottleMs = 60 * 1000;

  private readonly intervalTimeSecondsMs = import.meta.dev ? 30 * 1000 : 120 * 1000;

  /** Flag to prevent concurrent refresh operations */
  private isRefreshing = false;

  constructor() {
    this.boundVisibilityHandler = this.visibilityHandler.bind(this);
    this.documentVisibility = useDocumentVisibility();
  }

  /**
   * Initializes the refresh handler.
   * Will be called inside `app:mounted` lifecycle hook.
   */
  async init(): Promise<void> {
    this.userSession = useUserSession();

    // Watch for document visibility changes
    this.stopVisibilityWatcher = watch(
      this.documentVisibility,
      this.boundVisibilityHandler,
    );

    debugLog("🚤 Auth Plugin - performing initial session refresh");
    await nextTick();
    await this.callRefresh();

    debugLog("🚤 Auth Plugin - starting periodic session refresh");
    this.startRefreshInterval();
  }

  /**
   * Starts the periodic refresh interval timer
   */
  private startRefreshInterval(): void {
    this.refetchIntervalTimer = setInterval(async () => {
      if (this.documentVisibility.value === "hidden") {
        // No need for refresh while in background, visibility handler will take care of it
        return;
      }
      if (this.isRefreshing) {
        debugLog("🚤 Auth Plugin - timer refresh skipped (refresh already in progress)");
        return;
      }
      debugLog("🚤 Auth Plugin - refresh on timer");
      await this.callRefresh();
    }, this.intervalTimeSecondsMs);
  }

  /**
   * Clears the periodic refresh interval timer
   */
  private clearRefreshInterval(): void {
    if (this.refetchIntervalTimer) {
      clearInterval(this.refetchIntervalTimer);
      this.refetchIntervalTimer = undefined;
    }
  }

  /**
   * Handles cleanup of the refresh handler. Will be called on `unmount` app hook.
   */
  destroy(): void {
    this.clearRefreshInterval();
    if (this.stopVisibilityWatcher) {
      this.stopVisibilityWatcher();
      this.stopVisibilityWatcher = undefined;
    }
    this.userSession = undefined;
  }

  /**
   * Handles visibility changes to trigger session refresh.
   */
  async visibilityHandler(): Promise<void> {
    // Listen for when the page is visible, if the user switches tabs
    // and makes our tab visible again, re-fetch the session.
    if (this.documentVisibility.value !== "visible") {
      return;
    }

    // Check if at least X seconds have passed since the last refresh
    const now = Date.now();
    if (now - this.lastRefreshTimestamp < this.visibilityRefreshThrottleMs) {
      debugLog(`🚤 Auth Plugin - visibility refresh throttled (less than ${this.visibilityRefreshThrottleMs / 1000} seconds since last refresh)`);
      return;
    }
    if (this.isRefreshing) {
      debugLog("🚤 Auth Plugin - visibility refresh skipped (refresh already in progress)");
      return;
    }
    debugLog("🚤 Auth Plugin - refresh on visibility");
    this.clearRefreshInterval();
    await this.callRefresh();
    this.startRefreshInterval();
  }

  /**
   * Calls the session refresh logic and handles navigation if not authenticated.
   */
  async callRefresh(): Promise<void> {
    if (!this.userSession?.user.value || !this.userSession.fetch) {
      return;
    }
    try {
      this.isRefreshing = true;
      await this.userSession.fetch();
      this.lastRefreshTimestamp = Date.now();

      if (!this.userSession.loggedIn.value) {
        debugLog("🚤 Auth Plugin - not authenticated anymore after refresh");
        try {
          await navigateTo("/", { external: true });
        }
        catch (err) {
          console.error("🚤 Auth Plugin - navigation error after logout", err);
        }
      }
    }
    finally {
      this.isRefreshing = false;
    }
  }
}
